import React from "react";
import { Spinner } from "components/common/Spinner";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import { TransferTable } from "./TransferTypes";

interface TransferDialogBodyProps {
  dndHandlers: Pick<
    React.ComponentProps<typeof DndContext>,
    "onDragStart" | "onDragOver" | "onDragEnd"
  >;
  loading: boolean;
  error?: string | null;
  description?: React.ReactNode;
  layout?: "split" | "single-transfer";
  tablesPanel?: React.ReactNode;
  transferPanel: React.ReactNode;
  trashPanel?: React.ReactNode;
  activeDragTables: TransferTable[];
  dragActiveId: string | null;
  isOverTrash: boolean;
}

export const TransferDialogBody: React.FC<TransferDialogBodyProps> = ({
  dndHandlers,
  loading,
  error,
  description,
  layout = "split",
  tablesPanel,
  transferPanel,
  trashPanel,
  activeDragTables,
  dragActiveId,
  isOverTrash,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  return (
    <div className="flex flex-col h-[70vh]">
      <div className="mb-4 shrink-0">
        {description}
        {error && (
          <div className="mt-4 p-3 bg-error/10 border border-error/30 rounded text-error">
            {error}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          {...dndHandlers}
        >
          <div className="flex flex-col flex-1 min-h-0 space-y-4">
            {layout === "split" ? (
              <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
                {tablesPanel}
                {transferPanel}
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-auto">
                {transferPanel}
              </div>
            )}

            {trashPanel && (
              <div className="max-h-48 min-h-0 flex flex-col">{trashPanel}</div>
            )}
          </div>

          <DragOverlay>
            {dragActiveId && activeDragTables.length > 0 && (
              <div
                className={`opacity-50 bg-list-selected-light rounded-app p-2 max-h-64 overflow-y-auto transition-all duration-200 ${isOverTrash ? "w-155" : "w-72.5"}`}
              >
                <div className="flex flex-col gap-1">
                  {activeDragTables.map((table) => (
                    <div key={table.id} className="flex items-center gap-2">
                      <TableChartOutlinedIcon className="icon-sm" />
                      <span className="text-fg-regular">{table.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};
